import type {
    ICompanyInformationDto,
    ICustomerImpactDto,
    IRequestDto,
    RequestType,
} from 'generated/request-backend';
import { REQUEST_TYPE_KEYS } from 'app/modules/user/createTicket/contextualDetails/types';
import { StringConstants } from 'app/utils/constants';
import { RequestTypeVariant } from './types';

/**
 * Read-side view of a request DTO for the new ticket view surfaces.
 *
 * Every field is taken from the generated contract with `Pick`, so a rename or
 * removal on the backend fails the build here instead of silently rendering a
 * blank card. Only the shapes these surfaces actually read are picked, which
 * keeps the view models honest about what they depend on.
 *
 * The generated import is deliberately `import type`: `generated/request-backend`
 * pulls in the auth client (and MSAL) at module scope, and these helpers must
 * stay importable from plain unit tests. A type-only import is erased at compile
 * time, so the contract is still enforced with no runtime cost.
 */

/**
 * Fields collected by the new create-ticket flow that the contextual-details and
 * general-feedback cards render.
 */
export type PendingRequestFields = Pick<
    IRequestDto,
    | 'escalationReason'
    | 'warrantyHelpType'
    | 'warrantyAdditionalDetails'
    | 'businessConsequence'
    | 'customersPayMore'
    | 'estimatedPriceUplift'
    | 'keyCompetitors'
    | 'feedbackSource'
    | 'feedbackSourceDetails'
>;

/** Subset of `CompanyInformationDto` the view surfaces read. */
export type ViewCompanyInformation = Pick<
    ICompanyInformationDto,
    | 'companyContactName'
    | 'contactEmailAddress'
    | 'contactPhoneNumber'
    // Single-line shipping address; the API does not split street/city/postal.
    | 'companyFullShippingAddress'
    | 'country'
>;

/** Subset of `CustomerImpactDto` needed to decide whether a customer is attached. */
export type ViewCustomerImpact = Pick<ICustomerImpactDto, 'customerName'>;

/**
 * Structural read-model of a request as the view surfaces consume it.
 *
 * `requestType` is widened from the generated enum because a few list surfaces
 * (admin list-builder queries) hand back the display name instead of the value.
 */
export interface ViewRequest
    extends PendingRequestFields,
        Pick<
            IRequestDto,
            | 'cssCaseNumber'
            | 'confidentialIdentifier'
            | 'requestedResolutionDate'
            | 'whyThisDateIsImportant'
            | 'isParent'
            | 'submittedViaNewFlow'
        > {
    requestType?: RequestType | number | string;
    companyInformation?: ViewCompanyInformation;
    customerImpacts?: ViewCustomerImpact[];
}

/**
 * Widens any request-shaped object (a generated `RequestDto`, a plain list item,
 * or `undefined`) into the {@link ViewRequest} read-model. Always returns an
 * object so callers can read fields without null checks.
 */
export const toViewRequest = (request?: unknown): ViewRequest =>
    (request ?? {}) as ViewRequest;

/** Trimmed string value, or `''` when the field is missing/blank/non-string. */
export const readText = (value?: unknown): string =>
    typeof value === 'string' ? value.trim() : '';

/**
 * Maps a *persisted* request type onto the contextual-details variant to render
 * on the view surfaces.
 *
 * This is the read-side twin of the wizard's `getRequestTypeKey`, which maps the
 * in-flight store values instead. Only the three typed variants have a card
 * body; everything else (sales questions, CPE messaging/technical/program,
 * proposals, RFx, …) collapses to `other`, which renders no card at all.
 */

/**
 * The enum values are written as literals because importing the generated enum
 * for its *values* would pull the auth client into these plain helpers. The
 * assertions below tie each literal back to the generated `RequestType`, so a
 * renumbering on the backend breaks the build rather than silently dropping a
 * variant. They are types only and disappear at compile time.
 */
type AssertMatches<Actual extends Expected, Expected> = Actual;

/** CSSRequest, CssReviewRequest. */
export const CSS_REQUEST_TYPES: number[] = [6, 7];
type _AssertCssTypes = [
    AssertMatches<RequestType.CSSRequest, 6>,
    AssertMatches<RequestType.CssReviewRequest, 7>,
];

/** PostSales, CREW_WarrantyRequest. */
export const WARRANTY_REQUEST_TYPES: number[] = [5, 13];
type _AssertWarrantyTypes = [
    AssertMatches<RequestType.PostSales, 5>,
    AssertMatches<RequestType.CREW_WarrantyRequest, 13>,
];

/** CXP_PFR_Request, CREW_ProductRequest. */
export const FEATURE_REQUEST_TYPES: number[] = [9, 14];
type _AssertFeatureTypes = [
    AssertMatches<RequestType.CXP_PFR_Request, 9>,
    AssertMatches<RequestType.CREW_ProductRequest, 14>,
];

/**
 * Display names a request can arrive under. Both the UI labels in
 * `StringConstants` and the backend's own `[Description]` values are mapped,
 * because list-builder queries return the latter.
 */
const NAME_TO_VARIANT: Record<string, RequestTypeVariant> = {
    [StringConstants.CSS_REQUEST.toLowerCase()]: REQUEST_TYPE_KEYS.CSS,
    [StringConstants.CSS_REVIEW_REQUEST.toLowerCase()]: REQUEST_TYPE_KEYS.CSS,
    [StringConstants.REVIEW_REQUEST.toLowerCase()]: REQUEST_TYPE_KEYS.CSS,
    [StringConstants.REVIEW_REQUESTS.toLowerCase()]: REQUEST_TYPE_KEYS.CSS,
    [StringConstants.POST_SALES.toLowerCase()]: REQUEST_TYPE_KEYS.WARRANTY,
    [StringConstants.WARRANTY.toLowerCase()]: REQUEST_TYPE_KEYS.WARRANTY,
    // Enums.cs: CREW_WarrantyRequest = 13.
    'warranty request': REQUEST_TYPE_KEYS.WARRANTY,
    [StringConstants.PFR.toLowerCase()]: REQUEST_TYPE_KEYS.FEATURE,
    [StringConstants.FEATURE_REQUEST.toLowerCase()]: REQUEST_TYPE_KEYS.FEATURE,
    [StringConstants.PRODUCT.toLowerCase()]: REQUEST_TYPE_KEYS.FEATURE,
    // Enums.cs: CXP_PFR_Request = 9, CREW_ProductRequest = 14.
    'product feature request': REQUEST_TYPE_KEYS.FEATURE,
    'product request': REQUEST_TYPE_KEYS.FEATURE,
};

/** Resolves the contextual-details variant for a persisted request type. */
export const resolveRequestTypeVariant = (
    requestType?: RequestType | number | string,
): RequestTypeVariant => {
    if (typeof requestType === 'number') {
        if (CSS_REQUEST_TYPES.includes(requestType)) {
            return REQUEST_TYPE_KEYS.CSS;
        }
        if (WARRANTY_REQUEST_TYPES.includes(requestType)) {
            return REQUEST_TYPE_KEYS.WARRANTY;
        }
        if (FEATURE_REQUEST_TYPES.includes(requestType)) {
            return REQUEST_TYPE_KEYS.FEATURE;
        }
        return 'other';
    }

    if (typeof requestType === 'string') {
        return NAME_TO_VARIANT[requestType.trim().toLowerCase()] ?? 'other';
    }

    return 'other';
};

/** True when the request renders as a Feature Request / PFR. */
export const isFeatureRequestVariant = (
    requestType?: RequestType | number | string,
): boolean => resolveRequestTypeVariant(requestType) === REQUEST_TYPE_KEYS.FEATURE;
